import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { GetById } from "adminApi/ReturnInvoiceApi/ReturnInvoice";
import { GetById as GetCommonSetting } from "adminApi/CommonSettingApi/CommonSetting";
import Spinner from "Common/Globle/Spinner";
import html2pdf from "html2pdf.js";

// Format number to 2 decimal places safely
const fmt = (v) => Number(v || 0).toFixed(2);

const ReturnInvoiceView = ({ onPrintReady }) => {
  const { id } = useParams();
  const [values, setValues] = useState({});
  const [company, setCompany] = useState({});
  const [loadingFetch, setLoadingFetch] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const invoiceRef = useRef(null);

  // Fetch invoice + company info in parallel
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invoiceRes, settingRes] = await Promise.all([
          GetById(id),
          GetCommonSetting(1),
        ]);
        setValues(invoiceRes?.data?.data || {});
        setCompany(settingRes?.data || {});
      } catch (err) {
        console.error("Error fetching invoice:", err);
      } finally {
        setLoadingFetch(false);
      }
    };
    fetchData();
  }, [id]);

  // PDF generation — defined before useEffect that references it
  const handlePrintPDF = useCallback(async () => {
    const invoice = invoiceRef.current;
    if (!invoice) return;

    invoice.classList.add("pdf-mode");
    setPdfLoading(true);

    const options = {
      margin: 0,
      filename: `ReturnInvoice-${values.purchase_invoice_id || id}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 3, useCORS: true, backgroundColor: null },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    try {
      await html2pdf().set(options).from(invoice).save();
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      // Always remove pdf-mode and reset loading — even on error
      invoice.classList.remove("pdf-mode");
      setPdfLoading(false);
    }
  }, [values.purchase_invoice_id, id]);

  // Expose handlePrintPDF to parent via callback prop
  useEffect(() => {
    if (onPrintReady) onPrintReady(handlePrintPDF);
  }, [handlePrintPDF, onPrintReady]);

  const items = values.items || [];

  if (loadingFetch) return <Spinner isSpinner={true} />;

  return (
    <>
      {pdfLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}

      <div
        id="invoiceArea"
        ref={invoiceRef}
        className="invoice-wrapper p-3 p-md-4 border rounded shadow-sm bg-white"
      >
        {/* HEADER */}
        <div
          className="text-center py-4 rounded"
          style={{ backgroundColor: "#1A237E", color: "#fff" }}
        >
          <h2 className="fw-bold mb-0">PURCHASE RETURN INVOICE</h2>
          <small className="opacity-75">
            Invoice #{values.purchase_invoice_id}
          </small>
        </div>

        {/* COMPANY + SUPPLIER */}
        <div className="row g-4 mt-4">
          <div className="col-md-6">
            <h5 className="fw-bold mb-2">{company.site_text || "Company"}</h5>
            <p className="m-0 text-muted small">{company.address || ""}</p>
            {company.phone && (
              <p className="text-muted small">
                <i className="fa fa-phone me-2" />
                {company.phone}
              </p>
            )}
            {company.email && (
              <p className="text-muted small">
                <i className="fa fa-envelope me-2" />
                {company.email}
              </p>
            )}
          </div>

          <div className="col-md-6 text-md-end">
            <h5 className="fw-bold mb-2">Supplier Details</h5>
            <p className="fw-semibold m-0" style={{ color: "#1A237E" }}>
              {values.supplier_name}
            </p>
            {/* Use supplier_address — fixed typo from old model */}
            <p className="text-muted small m-0">
              {values.supplier_address || values.supplier_addres || ""}
            </p>
            {values.supplier_mobile && (
              <p className="text-muted small">
                <i className="fa fa-phone me-2" />
                {values.supplier_mobile}
              </p>
            )}
          </div>
        </div>

        <hr className="my-4" />

        {/* INVOICE DETAILS */}
        <div className="row mb-3">
          <div className="col-md-6 mb-2">
            <p className="text-muted small mb-1">Invoice No</p>
            <p className="fw-bold">{values.purchase_invoice_id}</p>
          </div>
          <div className="col-md-6 mb-2 text-md-end">
            <p className="text-muted small mb-1">Return Date</p>
            <p className="fw-bold">{values.return_date}</p>
          </div>
          <div className="col-md-6 mb-2">
            <p className="text-muted small mb-1">Reason</p>
            <p className="fw-bold">{values.reason || "-"}</p>
          </div>
        </div>

        {/* ITEMS TABLE */}
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead style={{ backgroundColor: "#1A237E", color: "#fff" }}>
              <tr>
                <th width="50">#</th>
                <th>Product</th>
                <th className="text-center">Quantity</th>
                <th className="text-center">Price</th>
                <th className="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.length > 0 ? (
                items.map((item, i) => (
                  <tr key={i}>
                    <td className="text-center fw-bold">{i + 1}</td>
                    <td>{item.product_name}</td>
                    <td className="text-center">{item.return_quantity}</td>
                    <td className="text-center">{fmt(item.price)}</td>
                    <td className="text-end fw-semibold">{fmt(item.amount)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-3 text-muted">
                    No Items Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* TOTALS */}
        <div className="row justify-content-end mt-4">
          <div className="col-md-5">
            <div className="d-flex justify-content-between py-2 border-bottom">
              <span className="text-muted">Total Amount</span>
              <span className="fw-bold">{fmt(values.total_amount)}</span>
            </div>
            <div className="d-flex justify-content-between py-2 border-bottom">
              <span className="text-muted">Discount</span>
              <span className="fw-bold">{fmt(values.total_discount)}</span>
            </div>
            <div className="d-flex justify-content-between p-3 mt-2 border rounded"
              style={{ background: "#F5F5F5" }}>
              <span className="fw-bold fs-5">Grand Total</span>
              <span className="fw-bold fs-4" style={{ color: "#2E7D32" }}>
                {fmt(values.grand_total)}
              </span>
            </div>
          </div>
        </div>

        <hr />
        <p className="text-center text-muted small">
          Thank you for your business!
        </p>
      </div>
    </>
  );
};

export default ReturnInvoiceView;
